import React, { FunctionComponent } from "react";
import styles from "./HeroBackground.module.scss";
import HeroBackgroundContent from "./HeroBackgroundContent.model";

import Anchor from "components/Anchor/Anchor";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import formatDynamicTextColor from "blocks/helpers/formatDynamicTextColor";
import getProcessedRichtext from "blocks/helpers/getProcessedRichtext";
import getWrapperStyles from "blocks/helpers/getWrapperStyles";
import getImageCrop from "app/media/helpers/getImageCrop";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import ImageViewer from "components/ImageViewer/ImageViewer";
import useWindowDimensions from "hooks/useWindowDimensions";

const HeroBackground: FunctionComponent<HeroBackgroundContent> = (props) => {
    const processedHeadline = getProcessedRichtext(formatDynamicTextColor(props.content.headline));
    const processedBodyText = getProcessedRichtext(props.content.bodyText);
    const wrapperStyles = getWrapperStyles(props.settings);

    const { width } = useWindowDimensions();
    const media = props.content.media;
    const imageCrop = getImageCrop(media?.crops, undefined, width < 768 ? "Square (1:1)" : width > 1366 ? "Ultrawide (21:9)" : "Fullscreen (4:3)");
    const imageAspectRatio = getImageAspectRatio(imageCrop, props.content.media?.width, props.content.media?.height);

    const heroHeightClass = props.content.heroHeight ? styles[props.content.heroHeight] : undefined;

    return (
        <div className={combineClasses([styles.wrapper, ...wrapperStyles])}>
            <div className={combineClasses([styles.inner, heroHeightClass])}>
                <div className={styles.container}>
                    {/* Tagline */}
                    {props.content.tagline ? <span className={styles.tagline}>{props.content.tagline}</span> : null}

                    {/* Headline */}
                    {props.content.headline ? (
                        <DynamicHeading
                            className={styles.headline}
                            headlineSize={props.content.headlineSize}
                            headlineWeight={props.content.headlineWeight}
                            innerHtml={processedHeadline}
                        ></DynamicHeading>
                    ) : null}

                    {/* Introductory */}
                    {props.content.introductory ? <p className={styles.introductory}>{props.content.introductory}</p> : null}

                    {/* Body Text */}
                    {processedBodyText ? (
                        <div
                            className={styles.bodyText}
                            dangerouslySetInnerHTML={processedBodyText}
                        ></div>
                    ) : null}

                    {/* Buttons */}
                    {props.content.buttons && props.content.buttons.length > 0 ? (
                        <div className={styles.buttons}>
                            {props.content.buttons.map((button) => {
                                return (
                                    <Anchor
                                        key={button.contentUdi}
                                        to={button.link}
                                        target={button.target}
                                        className={styles.button}
                                    >
                                        <span className={styles.button_label}>{button.label}</span>
                                    </Anchor>
                                );
                            })}
                        </div>
                    ) : null}
                </div>

                {/* Media */}
                <div className={styles.media}>
                    {media !== null ? (
                        <ImageViewer
                            className={styles.image}
                            src={media?.url || media}
                            crop={imageCrop}
                            focalPoint={media?.focalPoint}
                            {...imageAspectRatio}
                            alt={""}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default HeroBackground;
